import { signalStoreFeature, type, withMethods } from '@ngrx/signals';
import { WidgetState } from './widget.state';
import { inject } from '@angular/core';
import { IndicatorStore } from '../indicator/indicator.state';
import { DataLayerStore } from '../data-layer/data-layer.state';
import { IndicatorDataStore } from '../indicatpr-data/indicator-data.state';
import { WidgetHelperService } from '../../services/widget/widget-helper.service';
import { DataGroupProperty } from '../indicatpr-data/indicator-data.widget-selectors';
import { EntityId } from '@ngrx/signals/entities';
import { withWidgetCoreFeatures } from './widget.core-features';
import { makeUniqueStringArray } from '../../utility/array.utility';
import { ChartDataset } from 'chart.js';
import {
  CustomGridStackWidget,
  RegressionData,
  ScatterData,
  WegweiserData,
  WegweiserIndicator,
  WegweiserRegion,
  WidgetConfig,
  WidgetType,
} from '../../services/grid-widget-config.service';
import { ComparisonValueStore } from '../comparison-value/comparison-value.state';
import { ComparisonValue } from '../../models/geo-data';
import { updateAlpha } from '../../utility/color.utility';
import { DataLayer } from '../../models/data-layer';
import { GridStore } from './grid.state';
import { withWegweiserMethods } from './widget.wegweiser-methods';
import { withChartMetadataMethods } from './widget.chart-metadata-methods';
import { withChartAxisMethods } from './widget.chart-axis-methods';
import { applyDatasetStyling } from '../../utility/chart-styling.utility';
import {
  getMinValue,
  getMaxValue,
  normalize,
} from '../../utility/chart.utility';
import { ChartGroup, StyledDataset, BaseDataset } from './chart.types';

// ==================== UTILITY FUNCTIONS ====================

/**
 * Sorts chart group data including labels, keys, and datasets. Allows optional sorting
 * functionality based on specified criteria and direction.
 *
 * @param {string[]} labels - An array of chart labels corresponding to data points.
 * @param {string[]} keys - An array of unique keys corresponding to the chart labels.
 * @param {(BaseDataset | StyledDataset)[]} datasets - An array of dataset objects, each containing a label and array of numeric data values.
 * @param {boolean} enableSorting - A boolean value that determines whether sorting should be performed.
 * @param {'label'|'value'} [sortBy] - The criteria to sort by, either 'label' for sorting by label names, or 'value' for sorting by aggregate data values.
 * @param {'asc'|'desc'} [direction] - The sorting direction, either 'asc' for ascending or 'desc' for descending order.
 * @return {{ labels: string[]; datasets: (BaseDataset | StyledDataset)[]; groups: ChartGroup[] }} An object including the sorted labels array, updated datasets, and the computed groups data.
 */
function sortChartGroups(
  labels: string[],
  keys: string[],
  datasets: (BaseDataset | StyledDataset)[],
  enableSorting: boolean,
  sortBy?: 'label' | 'value',
  direction?: 'asc' | 'desc'
): {
  labels: string[];
  datasets: (BaseDataset | StyledDataset)[];
  groups: ChartGroup[];
} {
  // Create an array of objects representing groups
  const groups: ChartGroup[] = labels.map((label, index) => ({
    label: String(label),
    key: String(keys[index]),
    values: datasets.map(dataset => {
      const key = String(keys[index]);
      const dataIndex = dataset.dataKeys?.indexOf(key) || 0;
      return dataset.data[dataIndex] as number;
    }),
  }));

  if (!sortBy || !direction || !enableSorting) {
    return { labels, datasets, groups };
  }

  groups.sort((a, b) => {
    let compareA: string | number;
    let compareB: string | number;

    if (sortBy === 'label') {
      compareA = a.label.toLowerCase();
      compareB = b.label.toLowerCase();
    } else if (sortBy === 'value') {
      compareA = a.values!.reduce((sum, val) => sum + Number(val || 0), 0);
      compareB = b.values!.reduce((sum, val) => sum + Number(val || 0), 0);
    } else {
      throw new Error('Invalid sortBy criteria. Use "label" or "value".');
    }

    if (direction === 'asc') {
      return compareA < compareB ? -1 : compareA > compareB ? 1 : 0;
    } else {
      return compareA > compareB ? -1 : compareA < compareB ? 1 : 0;
    }
  });

  // Extract sorted labels and datasets
  const sortedLabels = groups.map(group => group.label);
  const sortedDatasets = datasets.map((dataset, datasetIndex) => ({
    ...dataset,
    data: groups.map(group => group.values![datasetIndex]),
    dataKeys: groups.map(group => group.key),
  }));

  return { labels: sortedLabels, datasets: sortedDatasets, groups };
}

export function withChartMethods() {
  return signalStoreFeature(
    {
      state: type<WidgetState>(),
    },
    withWidgetCoreFeatures(),
    withWegweiserMethods(),
    withChartMetadataMethods(),
    withChartAxisMethods(),
    withMethods(
      (
        store,
        layerStore = inject(DataLayerStore),
        indicatorDataStore = inject(IndicatorDataStore),
        gridStore = inject(GridStore),
        comparisonStore = inject(ComparisonValueStore),
        helper = inject(WidgetHelperService)
      ) => {
        // Destructure from store (includes methods from composed features)
        const {
          activeWidget,
          widgetType,
          selected,
          update,
          entityMap,
          get,
          getGroupedWegweiserData,
          getLabelsByGroupProperty,
          getKeysByGroupProperty,
          getDatasetLabel,
          getGroup,
          getLegendGroups,
          dataSetProperty,
          getValidKeys,
          convertToAxisData,
          calculateScatterData,
          addComparisonDatasets,
        } = store as any;

        /**
         * Creates a base dataset without styling (styling will be applied later after sorting).
         *
         * @param {Record<string, number | null>} data - The input data for the dataset.
         * @param {WidgetConfig} config - Configuration settings for the dataset.
         * @param {WidgetType} type - The widget type (e.g., chart type).
         * @param {string} labelKey - The key used to identify the label for the dataset.
         * @param {number} idx - The current index, used for color variations.
         * @param {string[]} dataKeys - An array of keys representing data fields used in the dataset.
         * @param {Record<string, ComparisonValue>} keyValueComparisonValues - A mapping of keys to their comparison values.
         * @param {DataGroupProperty} dsProperty - A property key that determines specific dataset behavior or grouping.
         *
         * @returns {StyledDataset} The formatted dataset object without styling applied.
         */
        const createDataset = (
          data: Record<string, number | null>,
          config: WidgetConfig,
          type: WidgetType,
          labelKey: string,
          idx: number,
          dataKeys: string[],
          keyValueComparisonValues: Record<string, ComparisonValue>,
          dsProperty: DataGroupProperty
        ): StyledDataset => {
          const label = getDatasetLabel(
            config,
            dsProperty,
            labelKey,
            false,
            keyValueComparisonValues
          );
          const tooltip = getDatasetLabel(
            config,
            dsProperty,
            labelKey,
            true,
            keyValueComparisonValues
          );

          const color = helper.getChartBackgroundColor(
            type,
            config.colorset,
            config.invertColors,
            idx,
            config.featureOpacity || 1,
            Object.values(data).length
          );

          return {
            data: Object.values(data),
            dataKeys: dataKeys,
            backgroundColor: color,
            borderColor: updateAlpha(
              color,
              config.featureOpacity ? config.featureOpacity + 0.4 : 1
            ),
            label: label,
            customTooltip: tooltip,
            labelKey: labelKey,
            featureType: dataSetProperty(config),
            pointRadius: config.dotSize || 4,
            borderWidth: config.borderWidth || 2,
            tension: config.lineTension || 0,
            group: getGroup(config, dsProperty, labelKey),
          };
        };

        /**
         * Applies styling to datasets based on selected and highlighted features.
         * This should be called AFTER sorting to ensure styling is applied to correct indices.
         *
         * @param {WidgetConfig} config - Configuration settings for the widget.
         * @param {WidgetType} type - The widget type (e.g., chart type).
         * @param featureData
         * @param comparisonData
         * @param {string} dsProperty - The dataset property type.
         * @param keyValueComparisonValues
         * @returns {any} The datasets with styling applied.
         */

        // ==================== MAIN UPDATE METHOD ====================

        const createLocalDatasets = (
          type: WidgetType,
          config: WidgetConfig,
          featureData: Record<string, any>,
          comparisonData: any,
          dsProperty: DataGroupProperty,
          keyValueComparisonValues: Record<string, ComparisonValue>
        ) => {
          let datasets: (BaseDataset | StyledDataset)[] = [];
          let layerIds: string[] = [];
          let dataKeys: string[] = [];

          if (helper.isAxisBasedType(type || 'adder')) {
            datasets =
              (convertToAxisData(
                featureData,
                config,
                type,
                keyValueComparisonValues
              ) as (BaseDataset | StyledDataset)[]) || [];
          } else {
            datasets = Object.values(featureData).map((item, idx) => {
              const labelKey = Object.keys(featureData)[idx];
              const setKeys = Object.keys(item);
              dataKeys = setKeys;

              layerIds = layerIds.concat(
                config.groupProperty === 'dataLayer'
                  ? Object.keys(item)
                  : Object.keys(featureData)
              );

              return createDataset(
                item,
                config,
                type,
                labelKey,
                idx,
                dataKeys,
                keyValueComparisonValues,
                dsProperty
              );
            });

            datasets = addComparisonDatasets(
              datasets,
              config,
              type,
              comparisonData,
              featureData
            );
          }

          return { datasets, layerIds, dataKeys };
        };

        const finalizeChartData = (
          config: WidgetConfig,
          type: WidgetType,
          datasets: (BaseDataset | StyledDataset)[],
          dataLayerIds: string[],
          dataKeys: string[],
          layerIds: string[],
          groupedData: any,
          dsProperty: DataGroupProperty,
          selectedFeatures: Array<{ key: string; type: string }> | null,
          highlightedFeature: { key: string; type: string } | undefined,
          widgetById: CustomGridStackWidget | null,
          rerender: boolean
        ) => {
          const labels = getLabelsByGroupProperty(
            config,
            dataLayerIds,
            dataKeys
          );
          const keys = getKeysByGroupProperty(config, dataLayerIds, dataKeys);

          // Sort the chart groups first
          config.chartData = sortChartGroups(
            labels as string[],
            keys as string[],
            datasets,
            config.sort.enable,
            config.sort.property,
            config.sort.direction
          );

          // Now apply styling based on the sorted keys
          const sortedKeys = config.chartData.groups.map(
            (group: ChartGroup) => group.key
          );
          config.chartData.datasets = applyDatasetStyling(
            config.chartData.datasets,
            config,
            type,
            sortedKeys,
            selectedFeatures,
            highlightedFeature,
            dsProperty,
            helper
          );

          config.chartData.legendGroups = getLegendGroups(config);
          config.chartData.minValue = getMinValue(datasets);
          config.chartData.maxValue = getMaxValue(datasets);
          config.groupedData = groupedData;
          config.dataLayers = makeUniqueStringArray(layerIds);
          config.dataSetProperty = dsProperty;

          const current = widgetById || { ...selected() };
          current.widget = config;
          update(current.id as string, current, rerender);
        };

        const updateChartData = (id?: EntityId, rerender = true) => {
          const selectedFeatures =
            gridStore.options()?.selectedFeatures || null;
          const highlightedFeature = gridStore.options()?.highlightedFeature;
          let widgetById: CustomGridStackWidget | null = null;
          const keyValueComparisonValues = comparisonStore.byKey();

          if (id) {
            widgetById = get(id);
          }

          const config = widgetById ? widgetById.widget : activeWidget();
          const type = widgetById ? widgetById.type : widgetType();

          if (!config || !type) return null;

          // force sort overwrite when group property is dataLayer (years)
          if (config.groupProperty === 'dataLayer') {
            config.sort = {
              enable: true,
              property: 'label',
              direction: 'asc',
            };
          }
          // Handle external/wegweiser data
          if (config.context !== 'local') {
            const data = getGroupedWegweiserData(id as string);

            if (data && data.datasets) {
              config.chartData = {
                datasets: data.datasets,
                labels: data.labels,
                minValue: getMinValue(data.datasets),
                maxValue: getMaxValue(data.datasets),
              };

              const current = widgetById || { ...selected() };
              current.widget = config;
              update(current.id as string, current, rerender);
            }
            return null;
          }

          // Handle local data
          const dsProperty = dataSetProperty(config);
          const groupedData = indicatorDataStore.groupByProperties(
            config[config.focusProperty],
            config.focusProperty,
            config.groupProperty,
            dsProperty,
            {
              indicators: config.indicators,
              disabledYears: config.disabledYears,
              dataLayerType: config.dataLayerType,
              disabledDataLayers: layerStore.getIdsByYears(
                config.dataLayerType,
                config.disabledYears
              ),
              disabledFeatures: config.disabledFeatures,
              availableFeatureKeys: Object.keys(config.features || []),
              comparisonKeys: Object.keys(keyValueComparisonValues),
            }
          );

          const { datasets, layerIds, dataKeys } = createLocalDatasets(
            type,
            config,
            groupedData.features,
            groupedData.comparison,
            dsProperty,
            keyValueComparisonValues
          );

          if (datasets) {
            finalizeChartData(
              config,
              type,
              datasets,
              groupedData.dataLayerIds,
              dataKeys,
              layerIds,
              groupedData,
              dsProperty,
              selectedFeatures,
              highlightedFeature,
              widgetById,
              rerender
            );
          }

          return null;
        };

        const getChartData = (id: EntityId) => {
          const widget = entityMap()[id];
          if (!widget || !widget.widget) return null;
          return widget.widget.chartData;
        };

        // ==================== RETURN METHODS ====================

        return {
          getLabelsByGroupProperty,
          getKeysByGroupProperty,
          getDatasetLabel,
          getGroup,
          getLegendGroups,
          dataSetProperty,
          updateChartData,
          calculateScatterData,
          convertToAxisData,
          getChartData,
        };
      }
    )
  );
}
